import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PartnerSettings } from "@/types/partner-settings";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { isPlatformAdminRole } from "@/utils/accessControl";
import { Role } from "@/types/user";

import {
  useEnablePartnerSettings,
  useDisablePartnerSettings,
  useUpdatePartnerSettings,
} from "../hooks/partner-settings.hooks";
import { PartnerDestinationSelect } from "./PartnerDestinationSelect";
import { SYNC_WAIT_MS } from "../utils/partnerDestinationSelect";
import type { PartnerDestination } from "@/types/partner-settings";

interface PartnerSectionFormProps {
  settings: PartnerSettings;
}

// Brand names — not translated, these are proper nouns. Rendered only for
// SUPER_ADMIN/GUESTERIA_ADMIN — every other role sees the fixed "Guesteria
// Partners" label instead (task 0028, contract 2026-09-28).
const providerLabels: Record<string, string> = {
  BOOKING_COM: "Booking.com",
  VIATOR: "Viator",
};

/**
 * Task 0036, contract 2026-09-29: sync management (Sync button, Auto Sync,
 * sync time/status, sync result toasts) and the destination search this
 * form used to carry are gone entirely — `autoSyncEnabled`/`lastSyncAt`
 * dropped from `PartnerSettings`, the hotel-scoped sync/logs endpoints
 * 404. Sync is Guesteria-admin-only now, managed from a dedicated admin
 * page (per-hotel sync status/trigger/auto-sync), not from here — for
 * SUPER_ADMIN/GUESTERIA_ADMIN this component is now just the provider
 * name, nothing left to save or trigger on this page. Every other role
 * keeps the one thing that's still theirs to control: the "Guesteria
 * Partners" enable/disable toggle (task 0028).
 */
export const PartnerSectionForm: React.FC<PartnerSectionFormProps> = ({
  settings,
}) => {
  const { t } = useTranslation();
  const { userProfile } = useAuthenticatedUser();
  const isPlatformAdmin = isPlatformAdminRole(userProfile);
  const isFrontDesk = userProfile?.role === Role.FRONT_DESK;

  const enablePartnerSettings = useEnablePartnerSettings();
  const disablePartnerSettings = useDisablePartnerSettings();
  const isTogglingPartners =
    enablePartnerSettings.isPending || disablePartnerSettings.isPending;

  const updatePartnerSettings = useUpdatePartnerSettings();
  const [syncWaiting, setSyncWaiting] = React.useState(false);
  React.useEffect(() => {
    if (!syncWaiting) return;
    const timer = setTimeout(() => setSyncWaiting(false), SYNC_WAIT_MS);
    return () => clearTimeout(timer);
  }, [syncWaiting]);

  // One change at a time: the selector is blocked while the save runs and
  // while the activity list of the new city is being built.
  const handleSelectCity = (destination: PartnerDestination) => {
    if (updatePartnerSettings.isPending || syncWaiting) return;
    updatePartnerSettings.mutate(
      {
        destinationId: destination.destinationId,
        destinationName: destination.name,
        destinationType: destination.type,
      },
      {
        onSuccess: () => {
          setSyncWaiting(true);
          toast.success(t("partners.hotelSettings.city.saveSuccess"));
        },
        onError: () => {
          toast.error(t("partners.hotelSettings.city.saveError"));
        },
      },
    );
  };

  let cityBusyText: string | null = null;
  if (updatePartnerSettings.isPending) {
    cityBusyText = t("partners.hotelSettings.city.saving");
  } else if (syncWaiting) {
    cityBusyText = t("partners.hotelSettings.city.syncing");
  }

  const handleTogglePartnersEnabled = (checked: boolean) => {
    const mutation = checked ? enablePartnerSettings : disablePartnerSettings;

    mutation.mutate(undefined, {
      onSuccess: () => {
        toast.success(
          t(
            checked
              ? "partners.hotelSettings.enableSuccess"
              : "partners.hotelSettings.disableSuccess",
          ),
        );
      },
      onError: () => {
        toast.error(
          t(
            checked
              ? "partners.hotelSettings.enableError"
              : "partners.hotelSettings.disableError",
          ),
        );
      },
    });
  };

  const providerLabel = settings.provider
    ? providerLabels[settings.provider] || settings.provider
    : t("partners.hotelSettings.title");
  const heading = isPlatformAdmin
    ? providerLabel
    : t("partners.hotelSettings.title");

  if (isPlatformAdmin) {
    return (
      <div className="flex flex-col gap-6">
        <h3 className="text-lg font-semibold">{heading}</h3>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-lg font-semibold">{heading}</h3>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Switch
            id={`partners-enabled-${settings.id}`}
            checked={settings.partnersEnabled}
            disabled={isFrontDesk || isTogglingPartners}
            onCheckedChange={
              isFrontDesk ? undefined : handleTogglePartnersEnabled
            }
          />
          <Label
            htmlFor={`partners-enabled-${settings.id}`}
            className="text-md"
          >
            {t("partners.hotelSettings.toggleLabel")}
          </Label>
        </div>
        <p className="text-xs leading-5 text-black/55">
          {t(
            isFrontDesk
              ? "partners.hotelSettings.readOnlyHint"
              : "partners.hotelSettings.toggleHint",
          )}
        </p>
      </div>
      {settings.partnersEnabled && (
        <PartnerDestinationSelect
          id={`partners-city-${settings.id}`}
          currentName={
            settings.destination?.destinationName ?? settings.destinationName
          }
          disabled={isFrontDesk}
          busy={updatePartnerSettings.isPending || syncWaiting}
          busyText={cityBusyText}
          onSelect={handleSelectCity}
        />
      )}
    </div>
  );
};
